import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiBook,
  FiArrowLeft,
  FiSearch,
  FiShuffle,
  FiChevronLeft,
  FiChevronRight,
  FiVolume2,
  FiBookmark,
  FiStar,
  FiFilter,
  FiGrid,
  FiList,
  FiCheck,
  FiX,
  FiTarget,
  FiAward,
  FiRefreshCw,
  FiPlay,
  FiClock,
  FiTrendingUp,
  FiGlobe,
  FiDownload,
  FiPrinter,
  FiSun,
  FiZap,
  FiMoon,
  FiEdit3,
  FiSmile,
  FiBarChart2,
} from 'react-icons/fi';
import { greWordsData as vocabularyData, greNuanceMap } from '../../../../utils/greVocabularyData';
import { setLocalStorageWithSync, speakWord } from '../../../../utils/utils';
import { renderNuanceInlineMarkdown } from '../../../../utils/renderNuanceInlineMarkdown';
import GREWordsChallenge from '../../../../components/GREWordsChallenge';
import { markHomeTaskEvent } from '../../../home/homeTaskStatus';
import { getChallengeWords } from '../api/progressAPI';
import liwazaLogo from '../../../../assets/liwaza-logo-white.png';
import '../styles/LessonStyles.css';
import './GREVocabularyLesson.css';

const CHALLENGE_POOL_LABELS = {
  last_7_days: 'Words from your articles this week',
  all_articles: 'All words from your articles so far',
  due: 'Words due for review',
  saved: 'Your saved words',
  starter: '30 words to get started',
  empty: 'No words available yet.',
};

const mapChallengeWord = (word) => ({
  word: word.word,
  definition_en: word.definition_en,
  definition_fr: word.definition_fr,
  usage_nuance_fr: word.usage_nuance_fr || '',
  usage_nuance_en: word.usage_nuance_en || '',
  synonyms: word.synonyms || [],
  difficulty: word.difficulty || 'Medium',
  question: word.question,
  options: word.options || [],
});

const matchBookWords = (names, book) => {
  const byKey = new Map(
    (book || []).map((word) => [String(word.word || '').toLowerCase(), word])
  );
  const seen = new Set();
  const matched = [];
  (names || []).forEach((name) => {
    const key = String(name || '').toLowerCase();
    if (!key || seen.has(key)) return;
    const word = byKey.get(key);
    if (!word) return;
    seen.add(key);
    matched.push(word);
  });
  return matched;
};

const pickRandomBookWords = (book, count) => {
  const unique = [];
  const seen = new Set();
  (book || []).forEach((word) => {
    const key = String(word?.word || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(word);
  });
  for (let i = unique.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [unique[i], unique[j]] = [unique[j], unique[i]];
  }
  return unique.slice(0, count);
};

// Spaced Repetition Algorithm (SM-2)
const calculateNextReview = (easeFactor, interval, quality) => {
  let newEaseFactor = easeFactor;
  let newInterval = interval;

  if (quality < 3) {
    newInterval = 1;
  } else {
    if (interval === 0) {
      newInterval = 1;
    } else if (interval === 1) {
      newInterval = 6;
    } else {
      newInterval = Math.round(interval * easeFactor);
    }
    newEaseFactor =
      easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    newEaseFactor = Math.max(1.3, newEaseFactor);
  }

  const nextReviewDate = new Date();
  nextReviewDate.setDate(nextReviewDate.getDate() + newInterval);

  return {
    easeFactor: newEaseFactor,
    interval: newInterval,
    nextReviewDate: nextReviewDate.toISOString(),
  };
};

const GREVocabularyLesson = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [savedWords, setSavedWords] = useState([]);
  const [masteredWords, setMasteredWords] = useState([]);
  const [wordProgress, setWordProgress] = useState({});
  const [showFlashcardMode, setShowFlashcardMode] = useState(false);
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [showDefinition, setShowDefinition] = useState(false);
  const [showWordsChallenge, setShowWordsChallenge] = useState(false);
  const [challengePool, setChallengePool] = useState(null);
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [showTranslation, setShowTranslation] = useState(false);
  const [flashcardMode, setFlashcardMode] = useState('word-first'); // 'word-first' or 'definition-first'
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [showProgressReport, setShowProgressReport] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false); // Default light mode
  const [wordNotes, setWordNotes] = useState({}); // User notes for words
  const [editingNoteWord, setEditingNoteWord] = useState(null); // Word currently being edited
  const [noteText, setNoteText] = useState('');
  const [noteEmoji, setNoteEmoji] = useState('');
  const [showNuanceActivity, setShowNuanceActivity] = useState(false);
  const [nuanceWords, setNuanceWords] = useState([]);
  const [nuanceCardIndex, setNuanceCardIndex] = useState(0);
  const [nuanceReviewDone, setNuanceReviewDone] = useState(false);
  const wordsPerPage = 30;

  const nuanceLookup = greNuanceMap;

  // Common emojis for quick selection
  const quickEmojis = [
    '📝',
    '⭐',
    '🔥',
    '💡',
    '🎯',
    '✅',
    '❌',
    '🤔',
    '💪',
    '🧠',
    '📚',
    '🎓',
  ];

  // Load theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('greVocabTheme');
    if (savedTheme !== null) {
      setIsDarkMode(savedTheme === 'dark');
    }
  }, []);

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('greVocabTheme', newTheme ? 'dark' : 'light');
  };

  // Check for PDF export parameter
  useEffect(() => {
    if (searchParams.get('export') === 'pdf') {
      setShowPDFPreview(true);
    }
  }, [searchParams]);

  // Check for challenge parameter to open the Words Challenge modal
  useEffect(() => {
    if (searchParams.get('challenge') === 'true') {
      // Small delay to ensure data is loaded
      const timer = setTimeout(() => {
        setShowWordsChallenge(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const userEmail =
    JSON.parse(sessionStorage.getItem('user'))?.email || 'default';

  // State for encountered words from VocabularyMastery
  const [encounteredWords, setEncounteredWords] = useState([]);
  // State for tracking article-based progress
  const [articleMasteredWords, setArticleMasteredWords] = useState([]);
  const [articleEncounteredWords, setArticleEncounteredWords] = useState([]);
  const [verbalProgressStats, setVerbalProgressStats] = useState({
    totalEncountered: 0,
    totalMastered: 0,
    articlesRead: 0,
  });

  // Load saved data from localStorage and VocabularyMastery progress
  useEffect(() => {
    console.log('userEmail is ', userEmail);
    // Load GRE Book local data
    const saved =
      JSON.parse(localStorage.getItem(`greVocabSavedWords_${userEmail}`)) || [];
    const mastered =
      JSON.parse(localStorage.getItem(`greVocabMasteredWords_${userEmail}`)) ||
      [];
    const progress =
      JSON.parse(localStorage.getItem(`greVocabProgress_${userEmail}`)) || {};
    const notes =
      JSON.parse(localStorage.getItem(`greVocabNotes_${userEmail}`)) || {};
    setWordNotes(notes);

    // Load encountered words from VocabularyMastery progress
    try {
      // Primary source: localStorage with liwaza_verbal_progress key
      let verbalProgressKey = `liwaza_verbal_progress_${userEmail}`;
      let verbalProgressData = localStorage.getItem(verbalProgressKey);

      console.log('verbalProgressKey is ', verbalProgressKey);
      console.log('verbalProgressData is ', verbalProgressData);
      // If not found, try to find any liwaza_verbal_progress key
      if (!verbalProgressData) {
        const allKeys = Object.keys(localStorage);
        const verbalProgressKeys = allKeys.filter((k) =>
          k.startsWith('liwaza_verbal_progress_')
        );
        if (verbalProgressKeys.length > 0) {
          verbalProgressKey = verbalProgressKeys[0];
          verbalProgressData = localStorage.getItem(verbalProgressKey);
          console.log('Found verbal progress with key:', verbalProgressKey);
        }
      }

      let rawData = verbalProgressData ? JSON.parse(verbalProgressData) : null;

      // Handle data wrapper - the localStorage structure is { data: {...}, timestamp, userEmail }
      let comprehensiveData = rawData?.data || rawData;

      console.log('comprehensiveData is ', comprehensiveData);

      // Fallback: check sessionStorage cache
      if (!comprehensiveData) {
        const sessionProgress = sessionStorage.getItem(
          'gre_comprehensive_verbal_progress'
        );
        const sessionRaw = sessionProgress ? JSON.parse(sessionProgress) : null;
        comprehensiveData = sessionRaw?.data || sessionRaw;
      }

      if (comprehensiveData) {
        console.log('Loaded verbal progress:', {
          totalMastered:
            comprehensiveData?.vocabulary_mastery?.total_words_mastered,
          totalEncountered:
            comprehensiveData?.vocabulary_mastery?.total_words_encountered,
          masteredWordsCount:
            comprehensiveData?.vocabulary_mastery?.mastered_words?.length,
        });
      }

      // Get mastered words from VocabularyMastery (from articles)
      const vocabMasteryMastered =
        comprehensiveData?.vocabulary_mastery?.mastered_words || [];
      const vocabMasteryMasteredStrings = vocabMasteryMastered.map((w) =>
        typeof w === 'string' ? w : w.word
      );

      // Get words needing practice (encountered but not mastered)
      const wordsNeedingPractice =
        comprehensiveData?.vocabulary_mastery?.words_needing_practice || [];
      const practiceStrings = wordsNeedingPractice.map((w) =>
        typeof w === 'string' ? w : w.word
      );

      // Track article-based progress separately
      setArticleMasteredWords(vocabMasteryMasteredStrings);
      setArticleEncounteredWords([
        ...new Set([...vocabMasteryMasteredStrings, ...practiceStrings]),
      ]);

      // Set verbal progress stats from comprehensive data
      setVerbalProgressStats({
        totalEncountered:
          comprehensiveData?.vocabulary_mastery?.total_words_encountered || 0,
        totalMastered:
          comprehensiveData?.vocabulary_mastery?.total_words_mastered || 0,
        articlesRead: comprehensiveData?.vocabulary_mastery?.articles_read || 0,
      });

      // Combine all encountered words (mastered + practice)
      const allEncountered = [
        ...new Set([...vocabMasteryMasteredStrings, ...practiceStrings]),
      ];
      setEncounteredWords(allEncountered);

      // Merge VocabularyMastery mastered words with local mastered words
      const combinedMastered = [
        ...new Set([...mastered, ...vocabMasteryMasteredStrings]),
      ];
      setMasteredWords(combinedMastered);
    } catch (error) {
      console.error('Error loading VocabularyMastery progress:', error);
      setMasteredWords(mastered);
    }

    setSavedWords(saved);
    setWordProgress(progress);
  }, [userEmail]);

  // Get all words with categories by first letter and encountered status
  const allWords = useMemo(() => {
    return vocabularyData.map((word) => {
      const nuance = nuanceLookup[word.word.toLowerCase()];
      return {
        ...word,
        category: word.word.charAt(0).toUpperCase() + ' Words',
        synonyms: word.synonyms || [],
        usage_nuance_fr: nuance?.usage_nuance_fr || word.usage_nuance_fr || '',
        usage_nuance_en: nuance?.usage_nuance_en || word.usage_nuance_en || '',
        isEncountered: encounteredWords.includes(word.word),
        isMastered: masteredWords.includes(word.word),
        isFromArticles: articleEncounteredWords.includes(word.word),
        isMasteredFromArticles: articleMasteredWords.includes(word.word),
        isNew:
          !encounteredWords.includes(word.word) &&
          !masteredWords.includes(word.word),
      };
    });
  }, [
    encounteredWords,
    masteredWords,
    articleEncounteredWords,
    articleMasteredWords,
    nuanceLookup,
  ]);

  // Check for nuance-activity parameter to open Nuance Discovery
  useEffect(() => {
    if (searchParams.get('nuance-activity') === 'true' && allWords.length > 0) {
      const timer = setTimeout(() => {
        const email =
          JSON.parse(sessionStorage.getItem('user'))?.email || 'default';
        const storageKey = `greNuanceDiscoveryHistory_${email}`;

        let presented = [];
        try {
          presented = JSON.parse(localStorage.getItem(storageKey)) || [];
        } catch (_) {
          presented = [];
        }

        const allNuanced = allWords.filter(
          (w) => w.usage_nuance_fr || w.usage_nuance_en
        );
        const presentedSet = new Set(presented);

        let candidates = allNuanced.filter(
          (w) => !presentedSet.has(w.word) && !w.isMastered
        );

        if (candidates.length === 0) {
          presented = [];
          localStorage.setItem(storageKey, JSON.stringify([]));
          candidates = allNuanced.filter((w) => !w.isMastered);
          if (candidates.length === 0) candidates = allNuanced;
        }

        const shuffled = [...candidates].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, 23);

        if (selected.length > 0) {
          const updatedPresented = [
            ...presented,
            ...selected.map((w) => w.word),
          ];
          localStorage.setItem(storageKey, JSON.stringify(updatedPresented));

          setNuanceWords(selected);
          setNuanceCardIndex(0);
          setNuanceReviewDone(false);
          setShowNuanceActivity(true);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams, allWords]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(allWords.map((w) => w.category))].sort();
    return [
      'all',
      'new',
      'encountered',
      'saved',
      'mastered',
      'learning',
      'due',
      ...cats,
    ];
  }, [allWords]);

  // Count of new words (never encountered)
  const newWordsCount = useMemo(() => {
    return allWords.filter((w) => w.isNew).length;
  }, [allWords]);

  // Count of encountered words (from articles, not yet mastered) - only words in the book
  const encounteredNotMasteredCount = useMemo(() => {
    return allWords.filter((w) => w.isEncountered && !w.isMastered).length;
  }, [allWords]);

  // Count of mastered words that exist in the book
  const masteredInBookCount = useMemo(() => {
    return allWords.filter((w) => w.isMastered).length;
  }, [allWords]);

  // Count of total mastered (including words not in book, for info display)
  const totalMasteredFromArticles = articleMasteredWords.length;

  // Filter words based on selected category, search, and difficulty
  const filteredWords = useMemo(() => {
    let words = allWords;

    // Category filter
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'new') {
        // Words never encountered before (not mastered, not in practice)
        words = words.filter((w) => w.isNew);
      } else if (selectedCategory === 'encountered') {
        // Words encountered from articles but not yet mastered
        words = words.filter((w) => w.isEncountered && !w.isMastered);
      } else if (selectedCategory === 'saved') {
        words = words.filter((w) => savedWords.includes(w.word));
      } else if (selectedCategory === 'mastered') {
        words = words.filter((w) => masteredWords.includes(w.word));
      } else if (selectedCategory === 'learning') {
        words = words.filter(
          (w) => !masteredWords.includes(w.word) && savedWords.includes(w.word)
        );
      } else if (selectedCategory === 'due') {
        const now = new Date();
        words = words.filter((w) => {
          const progress = wordProgress[w.word];
          if (!progress) return false;
          return new Date(progress.nextReviewDate) <= now;
        });
      } else {
        words = words.filter((w) => w.category === selectedCategory);
      }
    }

    // Difficulty filter
    if (difficultyFilter !== 'all') {
      words = words.filter(
        (w) => w.difficulty?.toLowerCase() === difficultyFilter.toLowerCase()
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      words = words.filter(
        (w) =>
          w.word.toLowerCase().includes(query) ||
          w.definition_en?.toLowerCase().includes(query) ||
          w.synonyms?.some((s) => s.toLowerCase().includes(query))
      );
    }

    return words;
  }, [
    allWords,
    selectedCategory,
    searchQuery,
    savedWords,
    masteredWords,
    wordProgress,
    difficultyFilter,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredWords.length / wordsPerPage);
  const paginatedWords = filteredWords.slice(
    (currentPage - 1) * wordsPerPage,
    currentPage * wordsPerPage
  );

  // Toggle word as saved
  const toggleSaved = useCallback(
    (word) => {
      setSavedWords((prev) => {
        const newSaved = prev.includes(word)
          ? prev.filter((w) => w !== word)
          : [...prev, word];
        setLocalStorageWithSync(
          `greVocabSavedWords_${userEmail}`,
          JSON.stringify(newSaved)
        );
        return newSaved;
      });
    },
    [userEmail]
  );

  // Toggle word as mastered
  const toggleMastered = useCallback(
    (word) => {
      setMasteredWords((prev) => {
        const newMastered = prev.includes(word)
          ? prev.filter((w) => w !== word)
          : [...prev, word];
        setLocalStorageWithSync(
          `greVocabMasteredWords_${userEmail}`,
          JSON.stringify(newMastered)
        );
        return newMastered;
      });
    },
    [userEmail]
  );

  // Open note editor for a word
  const openNoteEditor = useCallback(
    (word) => {
      const existingNote = wordNotes[word] || { text: '', emoji: '' };
      setNoteText(existingNote.text);
      setNoteEmoji(existingNote.emoji);
      setEditingNoteWord(word);
    },
    [wordNotes]
  );

  // Save note for a word
  const saveNote = useCallback(() => {
    if (!editingNoteWord) return;

    setWordNotes((prev) => {
      const newNotes = { ...prev };
      if (noteText.trim() || noteEmoji) {
        newNotes[editingNoteWord] = { text: noteText.trim(), emoji: noteEmoji };
      } else {
        delete newNotes[editingNoteWord];
      }
      setLocalStorageWithSync(
        `greVocabNotes_${userEmail}`,
        JSON.stringify(newNotes)
      );
      return newNotes;
    });

    setEditingNoteWord(null);
    setNoteText('');
    setNoteEmoji('');
  }, [editingNoteWord, noteText, noteEmoji, userEmail]);

  // Cancel note editing
  const cancelNoteEdit = useCallback(() => {
    setEditingNoteWord(null);
    setNoteText('');
    setNoteEmoji('');
  }, []);

  // Delete note for a word
  const deleteNote = useCallback(
    (word) => {
      setWordNotes((prev) => {
        const newNotes = { ...prev };
        delete newNotes[word];
        setLocalStorageWithSync(
          `greVocabNotes_${userEmail}`,
          JSON.stringify(newNotes)
        );
        return newNotes;
      });
      setEditingNoteWord(null);
      setNoteText('');
      setNoteEmoji('');
    },
    [userEmail]
  );

  // Update word progress for spaced repetition
  const updateWordProgress = useCallback(
    (word, quality) => {
      setWordProgress((prev) => {
        const current = prev[word] || { easeFactor: 2.5, interval: 0 };
        const updated = calculateNextReview(
          current.easeFactor,
          current.interval,
          quality
        );
        const newProgress = { ...prev, [word]: updated };
        setLocalStorageWithSync(
          `greVocabProgress_${userEmail}`,
          JSON.stringify(newProgress)
        );
        return newProgress;
      });
    },
    [userEmail]
  );

  // Flashcard mode functions
  const startFlashcards = useCallback(
    (shuffle = false) => {
      let cards = filteredWords.length > 0 ? filteredWords : allWords;
      if (shuffle) {
        cards = [...cards].sort(() => Math.random() - 0.5);
      }
      setCurrentFlashcardIndex(0);
      setShowDefinition(false);
      setShowFlashcardMode(true);
    },
    [filteredWords, allWords]
  );

  const nextFlashcard = useCallback(() => {
    const cards = filteredWords.length > 0 ? filteredWords : allWords;
    setCurrentFlashcardIndex((prev) => (prev + 1) % cards.length);
    setShowDefinition(false);
  }, [filteredWords, allWords]);

  const prevFlashcard = useCallback(() => {
    const cards = filteredWords.length > 0 ? filteredWords : allWords;
    setCurrentFlashcardIndex(
      (prev) => (prev - 1 + cards.length) % cards.length
    );
    setShowDefinition(false);
  }, [filteredWords, allWords]);

  // Handle flashcard rating (for spaced repetition)
  const rateFlashcard = useCallback(
    (quality) => {
      const cards = filteredWords.length > 0 ? filteredWords : allWords;
      const currentWord = cards[currentFlashcardIndex];
      updateWordProgress(currentWord.word, quality);
      if (quality >= 4) {
        if (!masteredWords.includes(currentWord.word)) {
          toggleMastered(currentWord.word);
        }
      }
      nextFlashcard();
    },
    [
      filteredWords,
      allWords,
      currentFlashcardIndex,
      updateWordProgress,
      masteredWords,
      toggleMastered,
      nextFlashcard,
    ]
  );

  // Latest book + saved list, read when the challenge opens so a progress
  // refresh does not remount the game mid-session.
  const challengeInputsRef = useRef({ allWords, savedWords });
  challengeInputsRef.current = { allWords, savedWords };

  // Challenge pool: articles from the last 7 days, then all article words,
  // then due reviews, then saved words. If none of those exist, 30 random words.
  useEffect(() => {
    if (!showWordsChallenge || nuanceReviewDone) return undefined;
    let cancelled = false;
    setChallengePool(null);

    const applyPool = (data, book, savedNames) => {
      const sessionSize = 30;
      const fromStudy = matchBookWords(data?.words, book);
      const saved = matchBookWords(savedNames, book);
      const seen = new Set(fromStudy.map((word) => word.word.toLowerCase()));
      const combined = [...fromStudy];
      if (combined.length < sessionSize) {
        saved.forEach((word) => {
          const key = word.word.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          combined.push(word);
        });
      }
      if (!combined.length) {
        const starter = pickRandomBookWords(book, sessionSize);
        if (!starter.length) {
          return {
            words: [],
            source: 'empty',
            label: CHALLENGE_POOL_LABELS.empty,
          };
        }
        return {
          words: starter.map(mapChallengeWord),
          source: 'starter',
          label: CHALLENGE_POOL_LABELS.starter,
        };
      }
      const addedSaved = combined.length > fromStudy.length;
      const source = fromStudy.length > 0 ? data?.source || 'all_articles' : 'saved';
      const baseLabel =
        CHALLENGE_POOL_LABELS[source] || CHALLENGE_POOL_LABELS.all_articles;
      return {
        words: combined.map(mapChallengeWord),
        source,
        label:
          addedSaved && fromStudy.length > 0
            ? `${baseLabel}. Saved words fill the rest.`
            : baseLabel,
      };
    };

    (async () => {
      let data = null;
      try {
        data = await getChallengeWords();
      } catch (error) {
        console.error('Error loading challenge words:', error);
      }
      if (cancelled) return;
      const { allWords: book, savedWords: savedNames } = challengeInputsRef.current;
      setChallengePool(applyPool(data, book, savedNames));
    })();

    return () => {
      cancelled = true;
    };
  }, [showWordsChallenge, nuanceReviewDone]);

  const startWordsChallenge = useCallback(() => {
    setShowWordsChallenge(true);
  }, []);

  // Get due words count
  const dueWordsCount = useMemo(() => {
    const now = new Date();
    return Object.entries(wordProgress).filter(
      ([, progress]) => new Date(progress.nextReviewDate) <= now
    ).length;
  }, [wordProgress]);

  // Render word card
  const renderWordCard = (word, index = 0) => {
    const isSaved = savedWords.includes(word.word);
    const isMastered = masteredWords.includes(word.word);
    const isNew = word.isNew;
    const isEncountered = word.isEncountered;
    const isFromArticles = word.isFromArticles;
    const progress = wordProgress[word.word];
    const wordNote = wordNotes[word.word];

    // Build card class names for proper highlighting
    const cardClasses = ['vocab-word-card'];
    if (isMastered) cardClasses.push('mastered-card');
    if (isEncountered && !isMastered) cardClasses.push('encountered-card');
    if (isSaved) cardClasses.push('saved');
    if (isNew) cardClasses.push('new-word');

    return (
      <div key={`${word.word}-${index}`} className={cardClasses.join(' ')}>
        <div className="vocab-word-header">
          <h3 className="vocab-word-title">
            {wordNote?.emoji && (
              <span className="vocab-word-emoji">{wordNote.emoji}</span>
            )}
            {word.word}
            {isMastered && (
              <span className="vocab-mastered-badge" title="Mastered">
                ✓
              </span>
            )}
            {isEncountered && !isMastered && (
              <span className="vocab-encountered-badge" title="In Progress">
                ◐
              </span>
            )}
          </h3>
          {isFromArticles && (
            <span
              className="vocab-source-badge"
              title="Encountered from reading articles"
            >
              📖
            </span>
          )}
          <div className="vocab-word-actions">
            <button
              className="vocab-action-btn"
              onClick={() => speakWord(word.word)}
              title="Pronounce"
            >
              <FiVolume2 />
            </button>
            <button
              className={`vocab-action-btn ${isSaved ? 'active' : ''}`}
              onClick={() => toggleSaved(word.word)}
              title={isSaved ? 'Remove from saved' : 'Save word'}
            >
              <FiBookmark />
            </button>
            <button
              className={`vocab-action-btn ${isMastered ? 'active mastered' : ''}`}
              onClick={() => toggleMastered(word.word)}
              title={isMastered ? 'Unmark as mastered' : 'Mark as mastered'}
            >
              <FiStar />
            </button>
            <button
              className={`vocab-action-btn ${wordNote ? 'active' : ''}`}
              onClick={() => openNoteEditor(word.word)}
              title="Add note or emoji"
            >
              <FiEdit3 />
            </button>
          </div>
        </div>

        {word.difficulty && (
          <span className={`vocab-difficulty ${word.difficulty.toLowerCase()}`}>
            {word.difficulty}
          </span>
        )}

        <p className="vocab-definition">{word.definition_en}</p>

        {word.synonyms && word.synonyms.length > 0 && (
          <div className="vocab-synonyms">
            <strong>Synonyms:</strong> {word.synonyms.slice(0, 4).join(', ')}
          </div>
        )}

        {showTranslation &&
          word.definition_fr &&
          word.definition_fr.trim() !== '' && (
            <p className="vocab-translation">
              <FiGlobe size={12} /> {word.definition_fr}
            </p>
          )}

        {(word.usage_nuance_fr || word.usage_nuance_en) && (
          <div className="vocab-nuance-box">
            <div className="vocab-nuance-header">
              <FiZap size={12} /> Usage nuance / Nuance
            </div>
            {word.usage_nuance_en && (
              <p className="vocab-nuance-text-en">
                <strong>EN:</strong>{' '}
                {renderNuanceInlineMarkdown(word.usage_nuance_en)}
              </p>
            )}
            {word.usage_nuance_fr && (
              <p className="vocab-nuance-text">
                <strong>FR:</strong>{' '}
                {renderNuanceInlineMarkdown(word.usage_nuance_fr)}
              </p>
            )}
          </div>
        )}

        {word.example_en &&
          !word.example_en.includes('The scholar demonstrated') && (
            <p className="vocab-example">
              <em>"{word.example_en}"</em>
            </p>
          )}

        {showTranslation && word.example_fr && (
          <p className="vocab-example-fr">
            <FiGlobe size={12} /> <em>"{word.example_fr}"</em>
          </p>
        )}

        {progress && (
          <div className="vocab-progress-indicator">
            <FiClock size={12} />
            <span>
              Next review:{' '}
              {new Date(progress.nextReviewDate).toLocaleDateString()}
            </span>
          </div>
        )}

        {wordNote?.text && (
          <div className="vocab-user-note">
            <FiEdit3 size={12} />
            <span>{wordNote.text}</span>
          </div>
        )}
      </div>
    );
  };

  // Render note editor modal
  const renderNoteEditor = () => {
    if (!editingNoteWord) return null;

    return (
      <div className="vocab-note-modal-overlay" onClick={cancelNoteEdit}>
        <div className="vocab-note-modal" onClick={(e) => e.stopPropagation()}>
          <div className="vocab-note-modal-header">
            <h3>
              <FiEdit3 /> Add Note for "{editingNoteWord}"
            </h3>
            <button className="vocab-note-close" onClick={cancelNoteEdit}>
              <FiX />
            </button>
          </div>

          <div className="vocab-note-modal-body">
            <div className="vocab-note-emoji-section">
              <label>Add an emoji:</label>
              <div className="vocab-emoji-input-row">
                <input
                  type="text"
                  className="vocab-emoji-input"
                  value={noteEmoji}
                  onChange={(e) => {
                    // Take the last emoji-like character if multiple entered
                    const value = e.target.value;
                    // Allow single emoji or clear
                    setNoteEmoji(value.slice(-2) || value);
                  }}
                  placeholder="Type or paste emoji..."
                  maxLength={4}
                />
                {noteEmoji && (
                  <button
                    className="vocab-emoji-clear"
                    onClick={() => setNoteEmoji('')}
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
              <div className="vocab-emoji-suggestions">
                <span className="vocab-emoji-suggestions-label">
                  Quick pick:
                </span>
                <div className="vocab-emoji-picker">
                  {quickEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      className={`vocab-emoji-btn ${noteEmoji === emoji ? 'active' : ''}`}
                      onClick={() =>
                        setNoteEmoji(noteEmoji === emoji ? '' : emoji)
                      }
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="vocab-note-text-section">
              <label htmlFor="noteText">Your personal note:</label>
              <textarea
                id="noteText"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a mnemonic, association, or any note to help you remember this word..."
                rows={4}
              />
            </div>
          </div>

          <div className="vocab-note-modal-footer">
            {wordNotes[editingNoteWord] && (
              <button
                className="vocab-note-delete-btn"
                onClick={() => deleteNote(editingNoteWord)}
              >
                <FiX /> Delete Note
              </button>
            )}
            <div className="vocab-note-actions">
              <button
                className="vocab-note-cancel-btn"
                onClick={cancelNoteEdit}
              >
                Cancel
              </button>
              <button className="vocab-note-save-btn" onClick={saveNote}>
                <FiCheck /> Save Note
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render progress report modal
  const renderProgressReport = () => {
    if (!showProgressReport) return null;

    // Calculate progress percentages
    const totalWords = allWords.length;
    const masteredPercent = ((masteredInBookCount / totalWords) * 100).toFixed(
      1
    );
    const inProgressPercent = (
      (encounteredNotMasteredCount / totalWords) *
      100
    ).toFixed(1);
    const savedPercent = ((savedWords.length / totalWords) * 100).toFixed(1);
    const newPercent = ((newWordsCount / totalWords) * 100).toFixed(1);

    // Words by difficulty
    const easyWords = allWords.filter(
      (w) => w.difficulty?.toLowerCase() === 'easy'
    );
    const mediumWords = allWords.filter(
      (w) => w.difficulty?.toLowerCase() === 'medium'
    );
    const hardWords = allWords.filter(
      (w) => w.difficulty?.toLowerCase() === 'hard'
    );

    const easyMastered = easyWords.filter((w) => w.isMastered).length;
    const mediumMastered = mediumWords.filter((w) => w.isMastered).length;
    const hardMastered = hardWords.filter((w) => w.isMastered).length;

    // Words with notes
    const wordsWithNotes = Object.keys(wordNotes).length;

    return (
      <div
        className="vocab-progress-modal-overlay"
        onClick={() => setShowProgressReport(false)}
      >
        <div
          className="vocab-progress-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="vocab-progress-modal-header">
            <h3>
              <FiBarChart2 /> Progress Report
            </h3>
            <button
              className="vocab-progress-close"
              onClick={() => setShowProgressReport(false)}
            >
              <FiX />
            </button>
          </div>

          <div className="vocab-progress-modal-body">
            {/* Overall Progress */}
            <div className="vocab-progress-section">
              <h4>Overall Progress</h4>
              <div className="vocab-progress-bar-container">
                <div className="vocab-progress-bar">
                  <div
                    className="vocab-progress-fill mastered"
                    style={{ width: `${masteredPercent}%` }}
                    title={`Mastered: ${masteredPercent}%`}
                  />
                  <div
                    className="vocab-progress-fill in-progress"
                    style={{ width: `${inProgressPercent}%` }}
                    title={`In Progress: ${inProgressPercent}%`}
                  />
                </div>
                <div className="vocab-progress-labels">
                  <span>
                    {masteredInBookCount + encounteredNotMasteredCount} /{' '}
                    {totalWords} words
                  </span>
                  <span>
                    {(
                      parseFloat(masteredPercent) +
                      parseFloat(inProgressPercent)
                    ).toFixed(1)}
                    % covered
                  </span>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="vocab-progress-stats-grid">
              <div className="vocab-progress-stat-card mastered">
                <div className="vocab-stat-icon">
                  <FiStar />
                </div>
                <div className="vocab-stat-info">
                  <span className="vocab-stat-value">
                    {masteredInBookCount}
                  </span>
                  <span className="vocab-stat-label">Mastered</span>
                  <span className="vocab-stat-percent">{masteredPercent}%</span>
                </div>
              </div>

              <div className="vocab-progress-stat-card in-progress">
                <div className="vocab-stat-icon">
                  <FiPlay />
                </div>
                <div className="vocab-stat-info">
                  <span className="vocab-stat-value">
                    {encounteredNotMasteredCount}
                  </span>
                  <span className="vocab-stat-label">In Progress</span>
                  <span className="vocab-stat-percent">
                    {inProgressPercent}%
                  </span>
                </div>
              </div>

              <div className="vocab-progress-stat-card saved">
                <div className="vocab-stat-icon">
                  <FiBookmark />
                </div>
                <div className="vocab-stat-info">
                  <span className="vocab-stat-value">{savedWords.length}</span>
                  <span className="vocab-stat-label">Saved</span>
                  <span className="vocab-stat-percent">{savedPercent}%</span>
                </div>
              </div>

              <div className="vocab-progress-stat-card new">
                <div className="vocab-stat-icon">
                  <FiTarget />
                </div>
                <div className="vocab-stat-info">
                  <span className="vocab-stat-value">{newWordsCount}</span>
                  <span className="vocab-stat-label">New Words</span>
                  <span className="vocab-stat-percent">{newPercent}%</span>
                </div>
              </div>
            </div>

            {/* Difficulty Breakdown */}
            <div className="vocab-progress-section">
              <h4>Progress by Difficulty</h4>
              <div className="vocab-difficulty-breakdown">
                <div className="vocab-difficulty-row">
                  <span className="vocab-difficulty-label easy">Easy</span>
                  <div className="vocab-difficulty-bar">
                    <div
                      className="vocab-difficulty-fill"
                      style={{
                        width: `${easyWords.length > 0 ? (easyMastered / easyWords.length) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <span className="vocab-difficulty-count">
                    {easyMastered}/{easyWords.length}
                  </span>
                </div>
                <div className="vocab-difficulty-row">
                  <span className="vocab-difficulty-label medium">Medium</span>
                  <div className="vocab-difficulty-bar">
                    <div
                      className="vocab-difficulty-fill"
                      style={{
                        width: `${mediumWords.length > 0 ? (mediumMastered / mediumWords.length) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <span className="vocab-difficulty-count">
                    {mediumMastered}/{mediumWords.length}
                  </span>
                </div>
                <div className="vocab-difficulty-row">
                  <span className="vocab-difficulty-label hard">Hard</span>
                  <div className="vocab-difficulty-bar">
                    <div
                      className="vocab-difficulty-fill"
                      style={{
                        width: `${hardWords.length > 0 ? (hardMastered / hardWords.length) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <span className="vocab-difficulty-count">
                    {hardMastered}/{hardWords.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Article Progress */}
            {verbalProgressStats.articlesRead > 0 && (
              <div className="vocab-progress-section">
                <h4>From Reading Articles</h4>
                <div className="vocab-article-stats">
                  <div className="vocab-article-stat">
                    <FiBook />
                    <span>
                      <strong>{verbalProgressStats.articlesRead}</strong>{' '}
                      articles read
                    </span>
                  </div>
                  <div className="vocab-article-stat">
                    <FiAward />
                    <span>
                      <strong>{totalMasteredFromArticles}</strong> words
                      mastered from articles
                    </span>
                  </div>
                  <div className="vocab-article-stat">
                    <FiTrendingUp />
                    <span>
                      <strong>{verbalProgressStats.totalEncountered}</strong>{' '}
                      words encountered
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Study Stats */}
            <div className="vocab-progress-section">
              <h4>Study Activity</h4>
              <div className="vocab-study-stats">
                <div className="vocab-study-stat">
                  <span className="vocab-study-label">Words with notes</span>
                  <span className="vocab-study-value">{wordsWithNotes}</span>
                </div>
                <div className="vocab-study-stat">
                  <span className="vocab-study-label">Due for review</span>
                  <span className="vocab-study-value">{dueWordsCount}</span>
                </div>
                <div className="vocab-study-stat">
                  <span className="vocab-study-label">
                    Learning (saved, not mastered)
                  </span>
                  <span className="vocab-study-value">
                    {
                      allWords.filter(
                        (w) =>
                          savedWords.includes(w.word) &&
                          !masteredWords.includes(w.word)
                      ).length
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="vocab-progress-section vocab-recommendations">
              <h4>💡 Recommendations</h4>
              <ul>
                {dueWordsCount > 0 && (
                  <li>
                    You have <strong>{dueWordsCount}</strong> words due for
                    review. Practice them to maintain mastery!
                  </li>
                )}
                {encounteredNotMasteredCount > 0 && (
                  <li>
                    <strong>{encounteredNotMasteredCount}</strong> words are in
                    progress. Use flashcards to master them!
                  </li>
                )}
                {hardWords.length > 0 &&
                  hardMastered < hardWords.length * 0.5 && (
                    <li>
                      Focus on <strong>hard</strong> words - you've mastered{' '}
                      {hardMastered} of {hardWords.length}.
                    </li>
                  )}
                {savedWords.length === 0 && (
                  <li>
                    Save words you want to focus on by clicking the bookmark
                    icon!
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="vocab-progress-modal-footer">
            <button
              className="vocab-progress-action-btn"
              onClick={() => {
                setShowProgressReport(false);
                startFlashcards(true);
              }}
            >
              <FiShuffle /> Practice Flashcards
            </button>
            <button
              className="vocab-progress-action-btn primary"
              onClick={() => {
                setShowProgressReport(false);
                startWordsChallenge();
              }}
            >
              <FiZap /> Word Challenge
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Handle PDF export - defined before conditional returns
  const handlePDFExport = useCallback(() => {
    setShowPDFPreview(true);
  }, []);

  // Generate printable content with Liwaza branding
  const printPDF = useCallback(() => {
    const printWindow = window.open('', '_blank');
    const wordsToExport = filteredWords.length > 0 ? filteredWords : allWords;

    // Check if word is mastered
    const isWordMastered = (word) => masteredWords.includes(word);

    // Generate word entry HTML
    const generateWordEntry = (word) => {
      const synonymsHTML =
        word.synonyms && word.synonyms.length > 0
          ? `<div class="word-synonyms"><strong>Synonyms:</strong> ${word.synonyms.slice(0, 5).join(', ')}</div>`
          : '';
      const exampleHTML = word.example_en
        ? `<div class="word-example">"${word.example_en}"</div>`
        : '';
      const masteredClass = isWordMastered(word.word) ? ' mastered' : '';

      return `
        <div class="word-entry${masteredClass}">
          <div class="word-title">
            ${word.word}
            <span class="word-difficulty ${(word.difficulty || 'medium').toLowerCase()}">${word.difficulty || 'Medium'}</span>
            ${isWordMastered(word.word) ? '<span class="mastered-badge">✓ Mastered</span>' : ''}
          </div>
          <div class="word-definition">${word.definition_en || ''}</div>
          ${synonymsHTML}
          ${exampleHTML}
        </div>
      `;
    };

    // Special Liwaza Pages
    const coverPage = `
      <div class="liwaza-page cover-page">
        <div class="liwaza-page-content">
          <div class="cover-logo-container">
            <img src="${liwazaLogo}" alt="Liwaza" class="liwaza-logo-img" onerror="this.style.display='none'" />
          </div>
          
          <div class="book-badge">GRE VOCABULARY</div>
          <h1 class="book-title">Liwaza GRE Vocabulary Book</h1>
          <p class="book-subtitle">Essential Vocabulary for Graduate School Success</p>
          
          <div class="book-stats">
            <div class="book-stat">
              <span class="stat-number">${wordsToExport.length}</span>
              <span class="stat-label">Essential Words</span>
            </div>
            <div class="book-stat">
              <span class="stat-number">322+</span>
              <span class="stat-label">Target Score</span>
            </div>
            <div class="book-stat">
              <span class="stat-number">100%</span>
              <span class="stat-label">Success Rate</span>
            </div>
          </div>
          
          <div class="book-description">
            <h3>What's Inside</h3>
            <ul>
              <li>✓ ${wordsToExport.length} carefully curated GRE vocabulary words</li>
              <li>✓ Clear definitions with examples</li>
              <li>✓ Synonyms for deeper understanding</li>
              <li>✓ Difficulty levels (Easy, Medium, Hard)</li>
            </ul>
          </div>
          
          <div class="book-contact">
            <p>WhatsApp: <strong>+33 6 05 90 63 91</strong> • Website: <strong>liwaza.org</strong></p>
          </div>
          
          <div class="book-footer">
            <p>© ${new Date().getFullYear()} Liwaza</p>
          </div>
        </div>
      </div>
    `;

    const founderPage = `
      <div class="liwaza-page founder-page">
        <div class="liwaza-page-content">
          <div class="cover-logo-container small">
            <img src="${liwazaLogo}" alt="Liwaza" class="liwaza-logo-img small" onerror="this.style.display='none'" />
          </div>
          <div class="page-badge">MEET THE FOUNDER</div>
          <h2 class="page-title">Patrick Sanang</h2>
          <p class="founder-role">Founder & CEO of Liwaza</p>
          
          <div class="founder-credentials">
            <span class="credential">INSEAD MBA</span>
            <span class="credential">Wharton MBA</span>
          </div>
          
          <div class="founder-story">
            <h3>The Story Behind Liwaza</h3>
            <p>
              Patrick experienced firsthand the challenges he is now solving with Liwaza. 
              After earning his MBA from INSEAD and Wharton, he witnessed how few Francophone 
              African students were represented in these world-class programs — despite the 
              immense talent on the continent.
            </p>
            <p>
              Determined to change this, he built Liwaza to give other talented Francophone 
              Africans the guidance, tools, and support they need to access top graduate 
              programs and pursue high-impact careers on the global stage.
            </p>
          </div>
          
          <div class="founder-mission">
            <h3>Our Mission</h3>
            <p>
              To democratize access to world-class graduate education for Francophone African 
              students by providing AI-powered preparation tools, personalized coaching, and 
              expert insights that level the playing field.
            </p>
          </div>
          
          <div class="supporters-section">
            <h3>Backed By</h3>
            <div class="supporters">
              <div class="supporter">
                <strong>INSEAD AI Venture Lab</strong>
                <span>AI-focused startup accelerator</span>
              </div>
              <div class="supporter">
                <strong>NVIDIA Inception Program</strong>
                <span>Cutting-edge AI startups</span>
              </div>
              <div class="supporter">
                <strong>Loyal VC</strong>
                <span>Evergreen venture fund</span>
              </div>
            </div>
          </div>
          
          <div class="page-contact">
            <p>Connect with us: <strong>contact@liwaza.org</strong></p>
          </div>
        </div>
      </div>
    `;

    const programsPage = `
      <div class="liwaza-page programs-page">
        <div class="liwaza-page-content">
          <div class="cover-logo-container small">
            <img src="${liwazaLogo}" alt="Liwaza" class="liwaza-logo-img small" onerror="this.style.display='none'" />
          </div>
          <div class="page-badge">PROGRAMS WE SUPPORT</div>
          <h2 class="page-title">Your Gateway to Top Graduate Schools</h2>
          <p class="page-subtitle">Liwaza helps students get into the world's best programs</p>
          
          <div class="programs-grid">
            <div class="program-card">
              <h4>MBA</h4>
              <p>Master of Business Administration</p>
            </div>
            <div class="program-card">
              <h4>Executive MBA</h4>
              <p>For experienced professionals</p>
            </div>
            <div class="program-card">
              <h4>MPA</h4>
              <p>Master of Public Administration</p>
            </div>
            <div class="program-card">
              <h4>MSc Finance</h4>
              <p>Master of Science in Finance</p>
            </div>
            <div class="program-card">
              <h4>MIM / PGE</h4>
              <p>Master in Management</p>
            </div>
            <div class="program-card">
              <h4>Specialized Masters</h4>
              <p>Industry-specific programs</p>
            </div>
          </div>
          
          <div class="schools-section">
            <h3>Students Admitted To</h3>
            <div class="schools-list">
              <span>Harvard</span>
              <span>Wharton</span>
              <span>INSEAD</span>
              <span>London Business School</span>
              <span>HEC Paris</span>
              <span>Cornell Johnson</span>
              <span>Michigan Ross</span>
              <span>ESSEC</span>
              <span>EDHEC</span>
              <span>ESCP</span>
            </div>
          </div>
          
          <div class="tests-section">
            <h3>Test Preparation</h3>
            <div class="tests-grid">
              <span class="test-badge">GRE</span>
              <span class="test-badge">GMAT</span>
              <span class="test-badge">TAGE MAGE</span>
              <span class="test-badge">IELTS</span>
              <span class="test-badge">TOEIC</span>
            </div>
          </div>
          
          <div class="success-stats">
            <div class="success-stat">
              <span class="stat-value">322+</span>
              <span class="stat-desc">Average GRE Score</span>
            </div>
            <div class="success-stat">
              <span class="stat-value">100%</span>
              <span class="stat-desc">Success Rate</span>
            </div>
            <div class="success-stat">
              <span class="stat-value">30+</span>
              <span class="stat-desc">Expert Coaches</span>
            </div>
          </div>
        </div>
      </div>
    `;

    const pricingPage = `
      <div class="liwaza-page pricing-page">
        <div class="liwaza-page-content">
          <div class="cover-logo-container small">
            <img src="${liwazaLogo}" alt="Liwaza" class="liwaza-logo-img small" onerror="this.style.display='none'" />
          </div>
          <div class="page-badge">FULL PACKAGE</div>
          <h2 class="page-title">Complete Your Graduate School Preparation</h2>
          
          <div class="pricing-card">
            <div class="price">
              <span class="currency">€</span>
              <span class="amount">70</span>
              <span class="period">/month</span>
            </div>
            <p class="pricing-tagline">Everything you need to succeed</p>
          </div>
          
          <div class="features-list">
            <h3>What's Included</h3>
            <ul>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>AI + Human Coaching</strong> to score 322+ in GRE</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>AI + Human Coach</strong> to achieve Band 7+ in IELTS with daily speaking practice</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>1-on-1 Coaching</strong> from top graduate school alumni</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>AI ATS + Human Coach</strong> for resume optimization</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>AI + Human Coach</strong> for essay optimization</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>Interview Preparation</strong> with mock sessions</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>Profile Evaluation</strong> for target schools</span>
              </li>
              <li>
                <span class="feature-bullet">•</span>
                <span class="feature-text"><strong>Priority Support</strong> on WhatsApp</span>
              </li>
            </ul>
          </div>
          
          <div class="cta-section">
            <p class="cta-text">Start today — join hundreds of successful students!</p>
            <div class="contact-info">
              <p>WhatsApp: <strong>+33 6 05 90 63 91</strong></p>
              <p>Visit: <strong>liwaza.org</strong></p>
              <p>Email: <strong>contact@liwaza.org</strong></p>
            </div>
          </div>
          
          <div class="guarantee">
            <p>Cancel anytime • Free community version available</p>
          </div>
        </div>
      </div>
    `;

    // Group words by letter and track positions for inserting special pages
    const grouped = {};
    wordsToExport.forEach((word) => {
      const letter = word.word.charAt(0).toUpperCase();
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(word);
    });

    // Generate word entries with special pages inserted
    let wordCount = 0;
    let hasInsertedFounder = false;
    let hasInsertedPrograms = false;
    let hasInsertedPricing = false;

    const wordEntriesHTML = Object.keys(grouped)
      .sort()
      .map((letter) => {
        let sectionHTML = '';

        // Check if we need to insert special pages before this section
        const wordsInSection = grouped[letter].length;

        // Insert founder page after ~10 words
        if (!hasInsertedFounder && wordCount >= 10) {
          sectionHTML += founderPage;
          hasInsertedFounder = true;
        }

        // Insert programs page after ~20 words
        if (!hasInsertedPrograms && wordCount >= 20) {
          sectionHTML += programsPage;
          hasInsertedPrograms = true;
        }

        // Insert pricing page after ~30 words
        if (!hasInsertedPricing && wordCount >= 30) {
          sectionHTML += pricingPage;
          hasInsertedPricing = true;
        }

        const wordsHTML = grouped[letter]
          .map((word) => generateWordEntry(word))
          .join('');

        wordCount += wordsInSection;

        return (
          sectionHTML +
          `
          <div class="section-header">${letter}</div>
          <div class="word-grid">${wordsHTML}</div>
        `
        );
      })
      .join('');

    // Liwaza design colors from index.css
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liwaza GRE Book - Essential Vocabulary</title>
        <style>
          @page {
            margin: 0.8cm;
            size: A4;
          }
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #1d1b20;
            background: #fefbff;
            padding: 0;
            margin: 0;
          }
          .page-wrapper {
            padding: 25px;
            min-height: 100vh;
            background: #fefbff;
          }
          
          /* Liwaza Special Pages */
          .liwaza-page {
            page-break-before: always;
            page-break-after: always;
            height: 100vh;
            max-height: 100vh;
            background: #262730;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 30px;
            margin: 0 -25px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            overflow: hidden;
            box-sizing: border-box;
          }
          .liwaza-page-content {
            text-align: center;
            max-width: 600px;
          }
          
          /* Logo Container */
          .cover-logo-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
          }
          .cover-logo-container.small {
            margin-bottom: 15px;
          }
          .liwaza-logo-img {
            height: 40px;
            width: auto;
          }
          .liwaza-logo-img.small {
            height: 32px;
          }
          
          .book-badge, .page-badge {
            display: inline-block;
            background: rgba(255,255,255,0.12);
            padding: 6px 16px;
            border-radius: 25px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 2px;
            margin-bottom: 15px;
            border: 1px solid rgba(255,255,255,0.15);
          }
          .book-title, .page-title {
            font-size: 32px;
            font-weight: 800;
            margin-bottom: 8px;
            letter-spacing: -1px;
            color: white;
          }
          .book-subtitle, .page-subtitle {
            font-size: 14px;
            opacity: 0.85;
            margin-bottom: 20px;
            color: rgba(255,255,255,0.85);
          }
          
          /* Cover Page Styles */
          .book-stats {
            display: flex;
            justify-content: center;
            gap: 25px;
            margin-bottom: 25px;
          }
          .book-stat {
            text-align: center;
          }
          .stat-number {
            display: block;
            font-size: 28px;
            font-weight: 800;
            color: #9b7dff;
          }
          .stat-label {
            font-size: 10px;
            opacity: 0.8;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: rgba(255,255,255,0.8);
          }
          .book-description {
            background: rgba(255,255,255,0.06);
            border-radius: 12px;
            padding: 18px;
            margin-bottom: 18px;
            text-align: left;
            border: 1px solid rgba(255,255,255,0.08);
          }
          .book-description h3 {
            font-size: 14px;
            margin-bottom: 10px;
            color: #9b7dff;
          }
          .book-description ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .book-description li {
            padding: 4px 0;
            font-size: 12px;
            color: rgba(255,255,255,0.9);
          }
          .book-contact {
            margin-bottom: 12px;
          }
          .book-contact p {
            margin: 0;
            font-size: 12px;
            color: rgba(255,255,255,0.9);
          }
          .book-contact strong {
            color: white;
          }
          .book-footer {
            opacity: 0.6;
            font-size: 10px;
            color: rgba(255,255,255,0.6);
          }
          
          /* Founder Page Styles */
          .founder-role {
            font-size: 14px;
            color: rgba(255,255,255,0.8);
            margin-bottom: 15px;
          }
          .founder-credentials {
            margin-bottom: 30px;
          }
          .credential {
            display: inline-block;
            background: rgba(155, 125, 255, 0.25);
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            margin: 0 5px;
            color: white;
          }
          .founder-story, .founder-mission {
            text-align: left;
            margin-bottom: 25px;
          }
          .founder-story h3, .founder-mission h3 {
            font-size: 14px;
            color: #9b7dff;
            margin-bottom: 12px;
          }
          .founder-story p, .founder-mission p {
            font-size: 13px;
            color: rgba(255,255,255,0.9);
            margin-bottom: 10px;
            line-height: 1.7;
          }
          .supporters-section h3 {
            font-size: 14px;
            color: #9b7dff;
            margin-bottom: 15px;
          }
          .supporters {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .supporter {
            background: rgba(255,255,255,0.06);
            padding: 12px 16px;
            border-radius: 10px;
            text-align: left;
            border: 1px solid rgba(255,255,255,0.08);
          }
          .supporter strong {
            display: block;
            font-size: 13px;
            color: white;
          }
          .supporter span {
            font-size: 11px;
            color: rgba(255,255,255,0.7);
          }
          .page-contact {
            margin-top: 25px;
            font-size: 13px;
            color: rgba(255,255,255,0.9);
          }
          .page-contact strong {
            color: white;
          }
          
          /* Programs Page Styles */
          .programs-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 30px;
          }
          .program-card {
            background: rgba(255,255,255,0.06);
            padding: 16px;
            border-radius: 12px;
            text-align: center;
            border: 1px solid rgba(255,255,255,0.08);
          }
          .program-card h4 {
            font-size: 14px;
            margin-bottom: 4px;
            color: #9b7dff;
            font-weight: 700;
          }
          .program-card p {
            font-size: 10px;
            color: rgba(255,255,255,0.7);
            margin: 0;
          }
          .schools-section, .tests-section {
            margin-bottom: 25px;
          }
          .schools-section h3, .tests-section h3 {
            font-size: 14px;
            color: #9b7dff;
            margin-bottom: 12px;
          }
          .schools-list {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px;
          }
          .schools-list span {
            background: rgba(255,255,255,0.08);
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 11px;
            color: rgba(255,255,255,0.9);
          }
          .tests-grid {
            display: flex;
            justify-content: center;
            gap: 10px;
            flex-wrap: wrap;
          }
          .test-badge {
            background: rgba(155, 125, 255, 0.25);
            padding: 8px 18px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 700;
            color: white;
          }
          .success-stats {
            display: flex;
            justify-content: center;
            gap: 25px;
          }
          .success-stat {
            text-align: center;
          }
          .stat-value {
            display: block;
            font-size: 28px;
            font-weight: 800;
            color: #9b7dff;
          }
          .stat-desc {
            font-size: 10px;
            color: rgba(255,255,255,0.8);
            text-transform: uppercase;
          }
          
          /* Pricing Page Styles */
          .pricing-card {
            background: rgba(155, 125, 255, 0.15);
            border: 2px solid rgba(155, 125, 255, 0.3);
            border-radius: 20px;
            padding: 30px;
            margin-bottom: 30px;
          }
          .price {
            display: flex;
            align-items: baseline;
            justify-content: center;
            gap: 2px;
          }
          .currency {
            font-size: 24px;
            font-weight: 600;
            color: white;
          }
          .amount {
            font-size: 64px;
            font-weight: 800;
            line-height: 1;
            color: #9b7dff;
          }
          .period {
            font-size: 16px;
            color: rgba(255,255,255,0.8);
          }
          .pricing-tagline {
            margin-top: 10px;
            font-size: 14px;
            color: rgba(255,255,255,0.9);
          }
          .features-list {
            text-align: left;
            margin-bottom: 30px;
          }
          .features-list h3 {
            font-size: 16px;
            color: #9b7dff;
            margin-bottom: 15px;
            text-align: center;
          }
          .features-list ul {
            list-style: none;
            padding: 0;
          }
          .features-list li {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 10px 0;
            border-bottom: 1px solid rgba(255,255,255,0.08);
            color: rgba(255,255,255,0.9);
          }
          .feature-bullet {
            font-size: 16px;
            flex-shrink: 0;
            color: #9b7dff;
            font-weight: bold;
          }
          .feature-text {
            font-size: 12px;
            line-height: 1.5;
            color: rgba(255,255,255,0.9);
          }
          .feature-text strong {
            color: #9b7dff;
          }
          .cta-section {
            margin-bottom: 20px;
          }
          .cta-text {
            font-size: 14px;
            margin-bottom: 15px;
            color: rgba(255,255,255,0.9);
          }
          .contact-info p {
            margin: 6px 0;
            font-size: 13px;
            color: rgba(255,255,255,0.9);
          }
          .contact-info strong {
            color: white;
          }
          .guarantee {
            font-size: 11px;
            color: rgba(255,255,255,0.7);
          }
          
          /* Regular Content Styles */
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding: 35px 25px;
            background: #262730;
            color: white;
            border-radius: 16px;
            border: 1px solid #e0e0e0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header-logo {
            height: 50px;
            margin-bottom: 15px;
          }
          .header h1 {
            margin: 0 0 8px 0;
            font-size: 28px;
            font-weight: 700;
            letter-spacing: -0.5px;
          }
          .header p {
            margin: 0;
            font-size: 14px;
            opacity: 0.9;
            font-weight: 500;
          }
          .stats {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 20px;
            flex-wrap: wrap;
          }
          .stats span {
            background: rgba(255,255,255,0.15);
            padding: 8px 18px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            border: 1px solid rgba(255,255,255,0.2);
          }
          .section-header {
            background: #625b71;
            color: white;
            padding: 10px 18px;
            margin: 25px 0 12px 0;
            border-radius: 10px;
            font-size: 18px;
            font-weight: 700;
            page-break-after: avoid;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .word-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .word-entry {
            background: #f8f1f6;
            padding: 14px 16px;
            border-radius: 12px;
            border: 1px solid #e0e0e0;
            page-break-inside: avoid;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .word-entry.mastered {
            border-left: 4px solid #4cb572;
            background: #f0f8f3;
          }
          .word-title {
            font-size: 16px;
            font-weight: 700;
            color: #262730;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }
          .word-difficulty {
            display: inline-block;
            font-size: 9px;
            padding: 2px 8px;
            border-radius: 10px;
            text-transform: uppercase;
            font-weight: 700;
            letter-spacing: 0.5px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .word-difficulty.easy { background: #a1d8b5; color: #1b5e20; }
          .word-difficulty.medium { background: #ffddb1; color: #854d0e; }
          .word-difficulty.hard { background: #f9dedc; color: #991b1b; }
          .mastered-badge {
            font-size: 9px;
            padding: 2px 8px;
            border-radius: 10px;
            background: #4cb572;
            color: white;
            font-weight: 600;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .word-definition {
            font-size: 12px;
            color: #4a4458;
            margin-bottom: 6px;
            line-height: 1.5;
          }
          .word-synonyms {
            font-size: 10px;
            color: #625b71;
            margin-bottom: 6px;
            background: #eaddf5;
            padding: 5px 8px;
            border-radius: 6px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .word-synonyms strong {
            color: #262730;
          }
          .word-example {
            font-size: 10px;
            color: #625b71;
            font-style: italic;
            padding: 6px 10px;
            background: #f8f1f6;
            border-left: 3px solid #625b71;
            border-radius: 0 6px 6px 0;
            margin-top: 6px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .footer {
            margin-top: 35px;
            padding: 25px;
            text-align: center;
            background: #262730;
            color: white;
            border-radius: 12px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .footer-logo-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 12px;
          }
          .footer-logo-img {
            height: 32px;
            width: auto;
          }
          .footer .brand {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 6px;
            color: rgba(255,255,255,0.9);
          }
          .footer p {
            margin: 4px 0;
            font-size: 11px;
            color: rgba(255,255,255,0.8);
          }
          .footer .website {
            margin-top: 12px;
            padding: 8px 20px;
            background: rgba(255,255,255,0.08);
            border-radius: 20px;
            display: inline-block;
            font-weight: 600;
            font-size: 12px;
            color: white;
          }
          @media print {
            body, .page-wrapper {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .liwaza-page {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .header, .section-header, .footer, .word-difficulty, .word-entry, .mastered-badge {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="page-wrapper">
          ${coverPage}
          
          
          ${wordEntriesHTML}
          
          <div class="footer">
            <div class="footer-logo-container">
              <img src="${liwazaLogo}" alt="Liwaza" class="footer-logo-img" onerror="this.style.display='none'" />
            </div>
            <p class="brand">Liwaza GRE Vocabulary Book</p>
            <p>© ${new Date().getFullYear()} Liwaza. All rights reserved.</p>
            <p>Your trusted partner for GRE preparation</p>
            <p class="website">app.liwaza.org • WhatsApp: +33 6 05 90 63 91</p>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [filteredWords, allWords, masteredWords]);

  // Flashcard Mode UI
  if (showFlashcardMode) {
    const cards = filteredWords.length > 0 ? filteredWords : allWords;
    const currentCard = cards[currentFlashcardIndex];

    if (!currentCard) {
      return (
        <div className="vocab-flashcard-container">
          <div className="vocab-flashcard-empty">
            <FiBook size={48} />
            <h3>No words to review</h3>
            <button
              className="vocab-btn primary"
              onClick={() => setShowFlashcardMode(false)}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="vocab-flashcard-container">
        <div className="vocab-flashcard-header">
          <button
            className="vocab-back-btn"
            onClick={() => setShowFlashcardMode(false)}
          >
            <FiArrowLeft /> Exit Flashcards
          </button>
          <div className="vocab-flashcard-progress">
            {currentFlashcardIndex + 1} / {cards.length}
          </div>
          <div className="vocab-flashcard-options">
            <button
              className={`vocab-mode-btn ${flashcardMode === 'word-first' ? 'active' : ''}`}
              onClick={() => setFlashcardMode('word-first')}
            >
              Word First
            </button>
            <button
              className={`vocab-mode-btn ${flashcardMode === 'definition-first' ? 'active' : ''}`}
              onClick={() => setFlashcardMode('definition-first')}
            >
              Definition First
            </button>
          </div>
        </div>

        <div
          className={`vocab-flashcard ${showDefinition ? 'flipped' : ''}`}
          onClick={() => setShowDefinition(!showDefinition)}
        >
          <div className="vocab-flashcard-front">
            {flashcardMode === 'word-first' ? (
              <>
                <h2 className="vocab-flashcard-word">{currentCard.word}</h2>
                <button
                  className="vocab-speak-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    speakWord(currentCard.word);
                  }}
                >
                  <FiVolume2 /> Pronounce
                </button>
                <p className="vocab-flashcard-hint">
                  Click to reveal definition
                </p>
              </>
            ) : (
              <>
                <p className="vocab-flashcard-definition-prompt">
                  {currentCard.definition_en}
                </p>
                <p className="vocab-flashcard-hint">Click to reveal word</p>
              </>
            )}
          </div>
          <div className="vocab-flashcard-back">
            {flashcardMode === 'word-first' ? (
              <>
                <p className="vocab-flashcard-definition">
                  {currentCard.definition_en}
                </p>
                {currentCard.synonyms && currentCard.synonyms.length > 0 && (
                  <p className="vocab-flashcard-synonyms">
                    <strong>Synonyms:</strong>{' '}
                    {currentCard.synonyms.slice(0, 4).join(', ')}
                  </p>
                )}
                {currentCard.example_en &&
                  !currentCard.example_en.includes(
                    'The scholar demonstrated'
                  ) && (
                    <p className="vocab-flashcard-example">
                      "{currentCard.example_en}"
                    </p>
                  )}
                {showTranslation &&
                  currentCard.definition_fr &&
                  currentCard.definition_fr.trim() !== '' && (
                    <p className="vocab-flashcard-fr">
                      <FiGlobe /> {currentCard.definition_fr}
                    </p>
                  )}
              </>
            ) : (
              <>
                <h2 className="vocab-flashcard-word">{currentCard.word}</h2>
                <button
                  className="vocab-speak-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    speakWord(currentCard.word);
                  }}
                >
                  <FiVolume2 /> Pronounce
                </button>
                {currentCard.synonyms && currentCard.synonyms.length > 0 && (
                  <p className="vocab-flashcard-synonyms">
                    <strong>Synonyms:</strong>{' '}
                    {currentCard.synonyms.slice(0, 4).join(', ')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="vocab-flashcard-rating">
          <p>How well did you know this?</p>
          <div className="vocab-rating-buttons">
            <button
              className="vocab-rating-btn again"
              onClick={() => rateFlashcard(1)}
            >
              <FiX /> Again
            </button>
            <button
              className="vocab-rating-btn hard"
              onClick={() => rateFlashcard(2)}
            >
              Hard
            </button>
            <button
              className="vocab-rating-btn good"
              onClick={() => rateFlashcard(3)}
            >
              Good
            </button>
            <button
              className="vocab-rating-btn easy"
              onClick={() => rateFlashcard(5)}
            >
              <FiCheck /> Easy
            </button>
          </div>
        </div>

        <div className="vocab-flashcard-nav">
          <button className="vocab-nav-btn" onClick={prevFlashcard}>
            <FiChevronLeft /> Previous
          </button>
          <button className="vocab-nav-btn" onClick={nextFlashcard}>
            Next <FiChevronRight />
          </button>
        </div>

        <div className="vocab-flashcard-actions">
          <button
            className={`vocab-action-btn-lg ${savedWords.includes(currentCard.word) ? 'active' : ''}`}
            onClick={() => toggleSaved(currentCard.word)}
          >
            <FiBookmark />{' '}
            {savedWords.includes(currentCard.word) ? 'Saved' : 'Save'}
          </button>
          <button
            className={`vocab-action-btn-lg ${masteredWords.includes(currentCard.word) ? 'active mastered' : ''}`}
            onClick={() => toggleMastered(currentCard.word)}
          >
            <FiStar />{' '}
            {masteredWords.includes(currentCard.word) ? 'Mastered' : 'Master'}
          </button>
        </div>
      </div>
    );
  }

  // Main Word List UI
  return (
    <div
      className={`gre-vocabulary-lesson ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
    >
      {/* PDF Preview Modal */}
      {showPDFPreview && (
        <div
          className="pdf-preview-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPDFPreview(false);
            }
          }}
        >
          <div className="pdf-preview-modal">
            <div className="pdf-preview-header">
              <h2>
                <FiDownload /> Export Liwaza GRE Book
              </h2>
              <button
                type="button"
                className="pdf-close-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPDFPreview(false);
                }}
              >
                <FiX />
              </button>
            </div>
            <div className="pdf-preview-content">
              <div className="pdf-preview-info">
                <div className="pdf-book-cover">
                  <img
                    src={liwazaLogo}
                    alt="Liwaza"
                    className="pdf-cover-logo"
                  />
                  <h3>Liwaza GRE Book</h3>
                  <p>Essential Vocabulary</p>
                </div>
                <div className="pdf-details">
                  <p>
                    <strong>{filteredWords.length || allWords.length}</strong>{' '}
                    words will be exported
                  </p>
                  <p>The PDF will include:</p>
                  <ul>
                    <li>✓ Word definitions</li>
                    <li>✓ Synonyms</li>
                    <li>✓ Example sentences</li>
                    <li>✓ Difficulty levels</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="pdf-preview-actions">
              <button
                type="button"
                className="pdf-cancel-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPDFPreview(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pdf-export-btn"
                onClick={(e) => {
                  e.preventDefault();
                  printPDF();
                }}
              >
                <FiPrinter /> Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="vocab-header">
        <button className="vocab-back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <div className="vocab-title-section">
          <h1>
            <FiBook /> Liwaza GRE Book
          </h1>
          <p className="vocab-subtitle">
            {allWords.length} essential words to master for the GRE
          </p>
        </div>
        <button className="vocab-export-btn" onClick={handlePDFExport}>
          <FiDownload /> Export PDF
        </button>
      </div>

      {/* Progress tracking info banner */}
      {(verbalProgressStats.totalEncountered > 0 ||
        masteredWords.length > 0) && (
        <div className="vocab-progress-banner">
          <div className="vocab-progress-banner-icon">
            <FiTrendingUp />
          </div>
          <div className="vocab-progress-banner-content">
            <span className="vocab-progress-banner-title">
              Your Progress is Synced
            </span>
            <span className="vocab-progress-banner-text">
              This book tracks your progress from reading articles and
              exercises.
              {verbalProgressStats.articlesRead > 0 && (
                <>
                  {' '}
                  You've read{' '}
                  <strong>{verbalProgressStats.articlesRead}</strong> articles
                </>
              )}
              {totalMasteredFromArticles > 0 && (
                <>
                  {' '}
                  and mastered <strong>{totalMasteredFromArticles}</strong>{' '}
                  words
                  {totalMasteredFromArticles > masteredInBookCount && (
                    <> ({masteredInBookCount} in this book)</>
                  )}
                  .
                </>
              )}
            </span>
          </div>
          <div className="vocab-progress-legend">
            <span className="legend-item mastered">
              <span className="legend-color"></span> Mastered
            </span>
            <span className="legend-item encountered">
              <span className="legend-color"></span> In Progress
            </span>
          </div>
        </div>
      )}

      <div className="vocab-stats-bar">
        <div
          className="vocab-stat"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedCategory('all')}
        >
          <FiBook />
          <span>{allWords.length} Total Words</span>
        </div>
        <div
          className="vocab-stat new-words"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedCategory('new')}
        >
          <FiTarget />
          <span>{newWordsCount} New Words</span>
        </div>
        {encounteredNotMasteredCount > 0 && (
          <div
            className="vocab-stat encountered"
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCategory('encountered')}
          >
            <FiPlay />
            <span>{encounteredNotMasteredCount} In Progress</span>
          </div>
        )}
        <div
          className="vocab-stat"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedCategory('saved')}
        >
          <FiBookmark />
          <span>{savedWords.length} Saved</span>
        </div>
        <div
          className="vocab-stat mastered"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedCategory('mastered')}
          title={
            totalMasteredFromArticles > masteredInBookCount
              ? `${masteredInBookCount} in this book (${totalMasteredFromArticles} total from articles)`
              : undefined
          }
        >
          <FiStar />
          <span>{masteredInBookCount} Mastered</span>
        </div>
        {dueWordsCount > 0 && (
          <div
            className="vocab-stat due"
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCategory('due')}
          >
            <FiClock />
            <span>{dueWordsCount} Due for Review</span>
          </div>
        )}
      </div>

      <div className="vocab-actions-bar">
        <button className="vocab-btn primary" onClick={startWordsChallenge}>
          <FiZap /> Word Challenge
        </button>
        <button
          className="vocab-btn secondary"
          onClick={() => startFlashcards(true)}
        >
          <FiShuffle /> Flashcards
        </button>
        {dueWordsCount > 0 && (
          <button
            className="vocab-btn due"
            onClick={() => {
              setSelectedCategory('due');
              startFlashcards(false);
            }}
          >
            <FiClock /> Review Due ({dueWordsCount})
          </button>
        )}
        <button
          className={`vocab-btn ${showTranslation ? 'active' : ''}`}
          onClick={() => setShowTranslation(!showTranslation)}
        >
          <FiGlobe /> {showTranslation ? 'Hide' : 'Show'} French
        </button>
        <button
          className="vocab-btn progress-report"
          onClick={() => setShowProgressReport(true)}
          title="View progress report"
        >
          <FiBarChart2 /> Progress
        </button>
        <button
          className="vocab-btn theme-toggle"
          onClick={toggleTheme}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <FiSun /> : <FiMoon />}
        </button>
      </div>

      <div className="vocab-controls">
        <div className="vocab-search">
          <FiSearch />
          <input
            type="text"
            placeholder="Search words, definitions, or synonyms..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="vocab-filters">
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
            className="vocab-select"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'all'
                  ? 'All Words'
                  : cat === 'new'
                    ? `New Words (${newWordsCount})`
                    : cat === 'encountered'
                      ? `In Progress (${encounteredNotMasteredCount})`
                      : cat === 'saved'
                        ? `Saved (${savedWords.length})`
                        : cat === 'mastered'
                          ? `Mastered (${masteredInBookCount})`
                          : cat === 'learning'
                            ? 'Learning'
                            : cat === 'due'
                              ? `Due for Review (${dueWordsCount})`
                              : cat}
              </option>
            ))}
          </select>

          <select
            value={difficultyFilter}
            onChange={(e) => {
              setDifficultyFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="vocab-select"
          >
            <option value="all">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <div className="vocab-view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>
        </div>
      </div>

      <div className="vocab-results-info">
        Showing {paginatedWords.length} of {filteredWords.length} words
      </div>

      <div className={`vocab-words-container ${viewMode}`}>
        {paginatedWords.map((word, index) => renderWordCard(word, index))}
      </div>

      {totalPages > 1 && (
        <div className="vocab-pagination">
          <button
            className="vocab-page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <FiChevronLeft />
          </button>
          <span className="vocab-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="vocab-page-btn"
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
          >
            <FiChevronRight />
          </button>
        </div>
      )}

      {/* GRE Words Challenge Modal (regular, not nuance mode) */}
      {showWordsChallenge && !nuanceReviewDone && !challengePool && (
        <div className="vocab-nuance-overlay">
          <div className="vocab-nuance-modal">
            <div className="vocab-nuance-modal-header">
              <h2><FiTarget /> GRE Words Challenge</h2>
              <button
                className="vocab-nuance-close"
                onClick={() => setShowWordsChallenge(false)}
              >
                <FiX />
              </button>
            </div>
            <p>Loading words from your articles…</p>
          </div>
        </div>
      )}

      {showWordsChallenge && !nuanceReviewDone && challengePool && (
        <GREWordsChallenge
          availableWords={challengePool.words}
          wordPoolLabel={challengePool.label}
          onClose={() => setShowWordsChallenge(false)}
          onSessionStarted={() => {
            markHomeTaskEvent(userEmail, 'gre-vocab-challenge', 'started');
            markHomeTaskEvent(userEmail, 'weekend-vocab', 'started');
          }}
          onSessionFinished={() => {
            markHomeTaskEvent(userEmail, 'gre-vocab-challenge', 'finished');
            markHomeTaskEvent(userEmail, 'weekend-vocab', 'finished');
          }}
          initialGameMode="riddle"
          initialWordCount={Math.min(
            30,
            Math.max(challengePool.words.length, 1)
          )}
          initialTimeBetweenQuestions={10}
          autoStart={false}
          showPremiumLimits={false}
          hideRiddleMode={false}
          showTextProgress={false}
          hideWordCountSelector={false}
        />
      )}

      {/* Nuance Discovery Activity Modal */}
      {showNuanceActivity && nuanceWords.length > 0 && !nuanceReviewDone && (
        <div className="vocab-nuance-overlay">
          <div className="vocab-nuance-modal">
            <div className="vocab-nuance-modal-header">
              <h2><FiZap /> Nuance Discovery</h2>
              <button
                className="vocab-nuance-close"
                onClick={() => setShowNuanceActivity(false)}
              >
                <FiX />
              </button>
            </div>

            <div className="vocab-nuance-progress-bar">
              <div
                className="vocab-nuance-progress-fill"
                style={{
                  width: `${((nuanceCardIndex + 1) / nuanceWords.length) * 100}%`,
                }}
              />
            </div>
            <span className="vocab-nuance-counter">
              {nuanceCardIndex + 1} / {nuanceWords.length}
            </span>

            <div className="vocab-nuance-card">
              <h3 className="vocab-nuance-word">
                {nuanceWords[nuanceCardIndex].word}
                <button
                  className="vocab-action-btn"
                  onClick={() => speakWord(nuanceWords[nuanceCardIndex].word)}
                  style={{ marginLeft: 8 }}
                >
                  <FiVolume2 />
                </button>
              </h3>
              <span
                className={`vocab-difficulty ${nuanceWords[nuanceCardIndex].difficulty?.toLowerCase()}`}
              >
                {nuanceWords[nuanceCardIndex].difficulty}
              </span>

              <div className="vocab-nuance-definitions">
                <p className="vocab-nuance-def-en">
                  <strong>EN:</strong> {nuanceWords[nuanceCardIndex].definition_en}
                </p>
                <p className="vocab-nuance-def-fr">
                  <FiGlobe size={13} /> {nuanceWords[nuanceCardIndex].definition_fr}
                </p>
              </div>

              {nuanceWords[nuanceCardIndex].example_en && (
                <p className="vocab-nuance-example">
                  <em>"{nuanceWords[nuanceCardIndex].example_en}"</em>
                </p>
              )}

              <div className="vocab-nuance-highlight">
                <div className="vocab-nuance-highlight-header">
                  <FiZap size={14} /> Usage nuance / Nuance d'utilisation
                </div>
                {nuanceWords[nuanceCardIndex].usage_nuance_en && (
                  <p className="vocab-nuance-en-text">
                    <strong>EN:</strong>{' '}
                    {renderNuanceInlineMarkdown(nuanceWords[nuanceCardIndex].usage_nuance_en)}
                  </p>
                )}
                {nuanceWords[nuanceCardIndex].usage_nuance_fr && (
                  <p>
                    <strong>FR:</strong>{' '}
                    {renderNuanceInlineMarkdown(nuanceWords[nuanceCardIndex].usage_nuance_fr)}
                  </p>
                )}
              </div>
            </div>

            <div className="vocab-nuance-nav">
              <button
                className="vocab-nuance-nav-btn"
                onClick={() => setNuanceCardIndex((i) => Math.max(0, i - 1))}
                disabled={nuanceCardIndex === 0}
              >
                <FiChevronLeft /> Previous
              </button>

              {nuanceCardIndex < nuanceWords.length - 1 ? (
                <button
                  className="vocab-nuance-nav-btn primary"
                  onClick={() => setNuanceCardIndex((i) => i + 1)}
                >
                  Next <FiChevronRight />
                </button>
              ) : (
                <button
                  className="vocab-nuance-nav-btn primary start-game"
                  onClick={() => {
                    setNuanceReviewDone(true);
                    setShowWordsChallenge(true);
                  }}
                >
                  <FiPlay /> Start Challenge
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Nuance Challenge (after reviewing cards) */}
      {nuanceReviewDone && showWordsChallenge && nuanceWords.length > 0 && (
        <GREWordsChallenge
          availableWords={nuanceWords.map((w) => ({
            word: w.word,
            definition_en: w.definition_en,
            definition_fr: w.definition_fr,
            usage_nuance_fr: w.usage_nuance_fr || '',
            usage_nuance_en: w.usage_nuance_en || '',
            synonyms: w.synonyms || [],
            difficulty: w.difficulty || 'Medium',
            question: w.question,
            options: w.options || [],
          }))}
          onClose={() => {
            setShowWordsChallenge(false);
            setNuanceReviewDone(false);
            setShowNuanceActivity(false);
          }}
          onSessionStarted={() => {
            markHomeTaskEvent(userEmail, 'gre-nuance-review', 'started');
            markHomeTaskEvent(userEmail, 'weekend-nuance', 'started');
          }}
          onSessionFinished={() => {
            markHomeTaskEvent(userEmail, 'gre-nuance-review', 'finished');
            markHomeTaskEvent(userEmail, 'weekend-nuance', 'finished');
          }}
          initialGameMode="riddle"
          initialWordCount={nuanceWords.length}
          initialTimeBetweenQuestions={15}
          autoStart={false}
          showPremiumLimits={false}
          hideRiddleMode={false}
          showTextProgress={false}
          hideWordCountSelector={true}
        />
      )}

      {/* Note Editor Modal */}
      {renderNoteEditor()}

      {/* Progress Report Modal */}
      {renderProgressReport()}
    </div>
  );
};

export default GREVocabularyLesson;
